import React, { useEffect, useState } from "react";

import { getLanguageValue } from "../../../utils/languagesUtilities";
import { getFormatValue } from "../../../utils/bookFormatsUtilities";
import { firstUpperOnly } from "../../../shared/utils/stringUtilities";
import useBrowserMode from "../../../shared/hooks/useBrowserMode";
import useScrollDetect from "../../../shared/hooks/useScrollDetect";
import useDirectionScroll from "../../../shared/hooks/useDirectionScroll";
import { normalizeTwoWords } from "../../../shared/utils/stringUtilities";

import "./podcastDescription.scss";

interface PodcastDescriptionProps {
  description: string;
  tags: Array<string>;
  author: string;
  series?: Array<string>;
  episodes?: string;
  duration?: string;
  format: string;
  language: string;
  tabs?: Array<any>;
  title: string;
}

const PodcastDescription = (props: PodcastDescriptionProps) => {
  const { description, tags, tabs = [], title } = props;

  const [infoRightSide, setInfoRightSide] = useState<any>([]);
  const [tabSelected, setTabSelected] = useState(0);
  const [upScroll, setUpScroll] = useState(false);
  const { isBrowser, window, document } = useBrowserMode();
  const { isScrollActive, refElement } = useScrollDetect();
  const { lastDirection } = useDirectionScroll();

  const refTabs = (tabs || []).map((rt: any) => rt.localizador);
  let timerScroll: any;

  useEffect(() => {
    if (props.description) {
      setInfoRightSide(getInfoFromProps);
    }
  }, [props]);

  useEffect(() => {
    isBrowser && document.addEventListener("scroll", onScrollAction);
    return () => {
      isBrowser && document.removeEventListener("scroll", onScrollAction);
    };
  }, [upScroll]);

  const getInfoObj = (title, content) => ({ title, content });

  const getInfoFromProps = () => {
    const author = props.author ? getInfoObj("Autor", props.author) : "";
    const series =
      props.series && props.series.length > 0
        ? getInfoObj("Serie", getSeries(props.series))
        : "";
    const episodes = props.episodes
      ? getInfoObj("Episodios", props.episodes)
      : "";
    const format = props.format
      ? getInfoObj("Formato", getFormatValue(props.format))
      : "";
    const language = props.language
      ? getInfoObj("Idiomas", firstUpperOnly(getLanguageValue(props.language)))
      : "";
    const duration = props.duration
      ? getInfoObj("Duración", props.duration)
      : "";

    const auxArr = [author, series, episodes, duration, format, language];
    return auxArr.filter((element) => element);
  };

  const getSeries = (seriesArray) => {
    const auxSeries = seriesArray.map((serie) => serie.trim());
    return auxSeries.join(". ");
  };

  const goToContent = (index: number) => {
    if (isBrowser && refTabs.length) {
      const element = document.getElementById(
        "DetallePodcast" + (refTabs[index][0] - 1)
      );
      setUpScroll(true);
      const headerElement = document.getElementsByClassName("m-header")[0];
      const headerHeight =
        window.innerWidth > 960
          ? 139
          : headerElement.className.indexOf("m-header__hide-desktop") !== -1
          ? 69
          : 139;
      window.scroll(0, element?.offsetTop - headerHeight);
    }
  };

  const verifyTabs = (index: number) => {
    const indexString = `${index}`;
    for (let i = 0; i < refTabs.length; i++) {
      const indexSearch = refTabs[i].find((rt) => rt === indexString);
      if (indexSearch !== undefined) {
        setTabSelected(i);
        return;
      }
    }
    setTabSelected(-1);
  };

  const onScrollAction = () => {
    if (tabs.length > 0) {
      window.clearTimeout(timerScroll);
      timerScroll = setTimeout(() => {
        if (upScroll) {
          setUpScroll(false);
        }
        const items = document.querySelectorAll(`.DetallePodcast`);
        if (window.pageYOffset < items[1].getBoundingClientRect().top) {
          setTabSelected(0);
          return;
        }
        for (let i = 0; i < items.length; i++) {
          const bottom = items[i].getBoundingClientRect().bottom;
          const top = items[i].getBoundingClientRect().top;
          if (bottom > 139 && top <= 139) {
            verifyTabs(i);
            return;
          }
        }
        setTabSelected(-1);
      }, 66);
    }
  };

  return (
    <section
      className={`o-podcast-desc ${
        tabs.length > 0 ? "o-podcast-desc--tabs" : ""
      }`}
      ref={refElement}
    >
      {tabs.length > 0 && (
        <ul
          className={`o-podcast-desc__tabs${
            isScrollActive ? " tabs-fixed" : ""
          }${lastDirection == -1 ? " tabs-fixed-move" : ""}`}
        >
          {tabs.map((value, index) => (
            <li
              key={`tab-${index}`}
              className={`o-podcast-desc__tabs__tab${
                index === tabSelected ? " tab-active" : ""
              } 
                gtmPodcast${normalizeTwoWords(title)}Tabs${normalizeTwoWords(
                value.nombreTag
              )}`}
              onClick={() => goToContent(index)}
            >
              {value.nombreTag}
            </li>
          ))}
        </ul>
      )}
      <div
        className={
          tabs.length > 0 ? "o-podcast-desc__content DetallePodcast" : ""
        }
        id="DetallePodcast-1"
      >
        <h2 className="o-podcast-desc__title">Descripción</h2>
        <div className="o-podcast-desc__columns">
          <div className="o-podcast-desc__left">
            <p className="o-podcast-desc__left__description">
              {description || "Muy pronto te contaremos más sobre este título."}
            </p>
            {tags.length > 0 && (
              <>
                <p className="o-podcast-desc__left__subjects-title">Tags</p>
                <ul className="o-podcast-desc__left__subjects-items">
                  {tags.slice(0, 10).map((subject, index) => (
                    <li
                      className="o-podcast-desc__left__subjects-items__item"
                      key={index}
                    >
                      {subject}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className="o-podcast-desc__vertical-divider"></div>
          <div className="o-podcast-desc__right">
            {infoRightSide.map((info, index) => (
              <div className="o-podcast-desc__right__item" key={index}>
                <p className="o-podcast-desc__right__item__title">
                  {info.title}
                </p>
                <p className="o-podcast-desc__right__item__content">
                  {info.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PodcastDescription;
